/**
 * FadeImage
 * =========
 * The site's <picture><source webp/><img/></picture> pattern, plus a
 * smooth opacity fade-in once the image actually finishes loading rather
 * than popping in mid-scroll. The wrapping <picture>/<img> keep their own
 * explicit sizing (aspect-ratio classes, absolute inset-0, etc. - whatever
 * the caller already used) so there's never a layout shift; only opacity
 * animates, never a dimension.
 */

import { useState, useRef, useEffect } from 'react';
import { toWebP } from '../../utils/imageHelpers';

const FadeImage = ({ src, alt, className = '', imgClassName = '', loading = 'lazy', ...props }) => {
  const imgRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  // Cached images can finish decoding before this effect (or even the
  // onLoad prop) gets attached - .complete catches that so a repeat visit
  // never gets stuck at opacity-0.
  useEffect(() => {
    if (imgRef.current?.complete) setLoaded(true);
  }, []);

  return (
    <picture className={className}>
      <source srcSet={toWebP(src)} type="image/webp" />
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        loading={loading}
        onLoad={() => setLoaded(true)}
        // transition-[opacity,transform] (not the separate transition-opacity
        // utility) deliberately: a caller passing its own transform utility
        // via imgClassName (e.g. a hover-zoom) would otherwise fight this
        // component's own transition-property declaration for the same CSS
        // property, and which one wins depends on Tailwind's internal
        // stylesheet order rather than anything visible here.
        className={`transition-[opacity,transform] duration-700 ease-out ${loaded ? 'opacity-100' : 'opacity-0'} ${imgClassName}`}
        {...props}
      />
    </picture>
  );
};

export default FadeImage;
